import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FuseLoadingService } from '@fuse/services/loading';
import { openSnackBar } from 'app/shared/utils/snackbar';
import { initial } from 'lodash';
import { ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  take,
} from 'rxjs/operators';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'nric-dropdown',
  template: `
    <div class="flex gap-1 flex-col">
      <label *ngIf="label" class="block text-sm font-medium text-gray-700">
        {{ label }}
        <span *ngIf="isRequired() && label !== ''" class="text-red-500">*</span>
      </label>
      <mat-form-field class="h-[52px]">
        <div class="flex flex-row items-center w-full pb-[15px]">
          <mat-select
            [formControl]="optionCtrl"
            (selectionChange)="onSelect($event)"
            [multiple]="multiple"
            [panelClass]="
              'custome-search-dropdown-panel-nocheck removecheckbox'
            "
          >
            <mat-option>
              <ngx-mat-select-search
                [formControl]="searchCtrl"
                placeholderLabel="Search NRIC"
                noEntriesFoundLabel="'No results found'. Press enter to create new record"
                (keydown.enter)="onEnter($event)"
              ></ngx-mat-select-search>
            </mat-option>

            <mat-option
              *ngFor="let item of filteredOptions | async"
              [value]="item"
            >
              {{ item.nirc_no }}
            </mat-option>
          </mat-select>
        </div>
      </mat-form-field>

      <div class="mt-[-22px]">
        <form-validator [controlName]="controlName"></form-validator>
      </div>
    </div>
  `,
})
export class NricDropdownComponent implements OnInit, OnDestroy {
  @Input() resource: any;
  @Input() customFilter: any = null;
  @Input() advFilter: any = null;
  @Input() controlName: FormControl = new FormControl('');
  @Input() label: string = 'NRIC No.';
  @Input() multiple: boolean = false;

  @Output() selectedOption: EventEmitter<any> = new EventEmitter();

  public optionCtrl: FormControl = new FormControl(null);
  public searchCtrl: FormControl = new FormControl('');
  public filteredOptions: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  private _onDestroy = new Subject<void>();
  private apiLoaded = false;

  constructor(
    private loadingService: FuseLoadingService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Set initial value from parent FormControl
    const initialValue = this.controlName?.value;
    if (Array.isArray(initialValue) && initialValue.length === 0) {
      this.optionCtrl.setValue(null);
    } else if (initialValue !== null && initialValue !== undefined) {
      this.optionCtrl.setValue(initialValue);

      if (this.multiple) {
        this.filteredOptions.next(
          Array.isArray(initialValue) ? initialValue : [initialValue]
        );
      } else {
        this.filteredOptions.next([initialValue]);
      }
    }
    // Search input changes
    this.searchCtrl.valueChanges
      .pipe(
        takeUntil(this._onDestroy),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((search) => this.loadOptions(search));

    // Sync optionCtrl with parent FormControl
    this.optionCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((val) => {
        if (this.controlName) {
          this.controlName.setValue(val);
        }
      });
  }

  loadOptions(search: string) {
    if (!search) return;
    const filter: any = { search: search || '' };
    if (this.customFilter) Object.assign(filter, this.customFilter);
    if (this.advFilter) Object.assign(filter, this.advFilter);

    if (!this.resource) return;

    this.loadingService.show();
    this.resource.list({ filter: JSON.stringify(filter) }).subscribe({
      next: (res) => {
        let options = res.data || [];

        // Ensure current selection is included in options
        const selected = this.optionCtrl.value;
        if (selected) {
          if (this.multiple) {
            (Array.isArray(selected) ? selected : [selected]).forEach((sel) => {
              if (!options.find((o) => o.nirc_no === sel.nirc_no)) {
                options = [sel, ...options];
              }
            });
          } else if (!options.find((o) => o.nirc_no === selected.nirc_no)) {
            options = [selected, ...options];
          }
        }

        this.filteredOptions.next(options);
        this.apiLoaded = true;
        this.loadingService.hide();
      },
      error: () => {
        openSnackBar('Failed to load NRIC options.', this.snackBar);
        this.loadingService.hide();
      },
    });
  }

  onSelect(event: any) {
    this.selectedOption.emit(event.value);
  }

  onEnter(event: KeyboardEvent) {
    event.preventDefault();
    if (!this.apiLoaded) return;

    const searchValue = this.searchCtrl.value?.trim();
    if (!searchValue) return;

    this.filteredOptions.pipe(take(1)).subscribe((currentOptions) => {
      let selectedOption = currentOptions.find(
        (o) => o.nirc_no === searchValue
      );

      if (!selectedOption) {
        // create a new option object in the same structure as the dropdown
        selectedOption = { nirc_no: searchValue };
        // add it to filteredOptions so it behaves like a real option
        this.filteredOptions.next([selectedOption, ...currentOptions]);
      }

      if (this.multiple) {
        const currentValue = this.optionCtrl.value || [];
        if (!currentValue.some((v) => v.nirc_no === selectedOption!.nirc_no)) {
          this.optionCtrl.setValue([...currentValue, selectedOption]);
        }
      } else {
        this.optionCtrl.setValue(selectedOption);
      }

      // Emit to parent
      this.selectedOption.emit(this.optionCtrl.value);

      // Clear search input
      this.searchCtrl.setValue('');
    });
  }
  public isRequired(): boolean {
    // check form if they have a required validation
    return this.controlName.hasValidator(Validators.required);
  }
  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
